/*
  "סמוך עלי" (stage א12): a whole purchase in one conversation, inside the
  AMOR BY YOU box of the home banner (snippets/trust-chat.liquid). It stands
  on its own: the request field, its pills and Your Private Edit do not change.

  - Opening: the button in the box opens a <dialog> that grows out of the box;
    the banners stop changing while it is open. The close button, a click
    outside the window and Esc go back to the page.
  - The brain is the AI server component as it is, through window.AmorAI.
    Free text goes to /ask, as from the request field; a look comes back with
    its /edit result and shows here as one compact card. Text after a look is
    a refinement: /edit gets the request, what was asked before in this
    conversation, and the current looks. The answer to a question ("women or
    men?") goes to /edit with it. Other intents show AMOR's sentence and the
    action as a button; nothing leaves the page by itself. Errors use the
    answer card's sentences.
  - The bag is Shopify's. A signed-in visitor's items go in at her profile
    sizes, written in the message; a guest, or a size missing or sold out,
    gets the existing size sheet over the conversation (look.js), and its
    cart:added is kept from opening the bag drawer.
  - The payment is mock. A signed-in visitor picks the office or home, sees
    the bag's summary as the bag shows it (the club benefit, the delivery
    date) and pays: a mock order number comes back, no order is made and the
    bag is emptied. A guest can sign in, and the conversation goes on, or go
    to the store's checkout.
  - After every AMOR message the field takes free text, and pills that fit
    the moment sit under it. In the cart and payment steps short answers
    ("כן", "יאללה", "לא") move the step on; any other text goes to the brain.
  - The conversation is kept in DemoStore with the signed-in state it was
    written in, so closing and opening goes on. "New conversation", ?reset=1
    and signing in or out clear it, except a sign-in started from here.
  - Stage א12, part 2: the same conversation opens from a quiet line on a
    product page (snippets/trust-line, [data-trust-open="product"]), with an
    opening about the item, "what goes with it" (/look, around the item in the
    color chosen on the page), shipping and returns (/ask) and a fixed answer
    about a fitting; and from a line at the top of the content pages
    ([data-trust-open="topic"]), with an opening and questions of the page's
    subject. Each place keeps its own conversation (home, product:<handle>,
    topic:<name>).
*/
(() => {
  const TEXT_MAX = 300;
  const REFINE_MAX = 100;
  const PAY_MS = 1200;
  const LIMITS = ['rate_limited', 'site_limit'];
  const EDIT_INTENTS = ['occasion_look', 'image_inspiration', 'gift'];
  const MODES = { occasion_look: 'occasion', image_inspiration: 'image', gift: 'gift' };
  const CHECKOUT_URL = '/checkout?locale=he';
  const DIRECTIONS = ['understated', 'statement', 'evening'];
  const root = window.Shopify?.routes?.root || '/';

  const store = () => window.DemoStore;
  const ai = () => window.AmorAI;
  const normalize = (text) => String(text || '').replace(/\s+/g, ' ').trim();
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const hoverDevice = window.matchMedia('(hover: hover)');
  const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));
  const fill = (text, values) =>
    Object.entries(values).reduce((out, [key, value]) => out.split(`__${key.toUpperCase()}__`).join(value ?? ''), String(text || ''));

  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  };

  const cssValue = (name) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  // Shopify serves the CSS minified: "500ms" arrives as ".5s".
  const cssTime = (name, fallback) => {
    const value = cssValue(name);
    const number = parseFloat(value);
    if (!Number.isFinite(number)) return fallback;
    return /ms$/.test(value) ? number : number * 1000;
  };

  /* --- Short answers (section 7): every word must be one of these ----------- */
  const words = (text) =>
    normalize(text)
      .toLowerCase()
      .replace(/[.,!?״"'׳:;()\-–]/g, ' ')
      .split(/\s+/)
      .filter(Boolean);
  const YES = new Set(
    ('כן יאללה בטח סבבה אוקיי אוקי ok okay yes תוסיפי תוסיף תוסיפו הוסיפי להוסיף בבקשה מעולה יופי קדימה אחלה ברור ' +
      'בדיוק מושלם כמובן נשמע טוב בסדר לשלם תשלמי שלמי לתשלום תשלום לסל את זה אותו אותם הכל הכול תודה אהבתי ' +
      'נו אז גם רוצה ממשיכים להמשיך המשך')
      .split(' ')
  );
  const NO = new Set('לא תודה עכשיו בינתיים כרגע צריך צריכה רוצה ממש זה עוד אולי'.split(' '));
  const only = (list, set) => list.length > 0 && list.length <= 5 && list.every((word) => set.has(word));
  const answerOf = (text) => {
    const list = words(text);
    if (list[0] === 'לא' && only(list, NO)) return 'no';
    if (!list.includes('לא') && only(list, YES)) return 'yes';
    return null;
  };
  const has = (text, pattern) => words(text).length <= 5 && pattern.test(normalize(text));
  const OTHER = /^(?:עוד |ל)?(?:כיוון|לוק|סגנון|משהו) (?:אחר|שני)\??$/;
  const OFFICE = /משרד|לעבודה|כרגיל/;
  const HOME = /הבית|לבית|בבית/;
  const LOGIN = /להתחבר|התחבר|כניסה|להיכנס|אתחבר/;
  const REGISTER = /קופה/;
  // After a look, a question about the store goes to the brain as a question, not as a change to the look.
  const INFO = new Set(
    'משלוח משלוחים החזרה החזרות להחזיר החלפות שעות בוטיק מועדון נציג נציגה הזמנה הזמנות מדידה ביטול לבטל טלפון כתובת מדריך gift'.split(' ')
  );
  const isInfo = (text) => words(text).some((word) => INFO.has(word) || INFO.has(word.replace(/^[והבלש]{1,2}/, '')));

  /** What /edit gets back as `previous` when refining: the items, by handle and color. */
  const previousOf = (result) => ({
    looks: (result.looks || []).map((look) => ({
      items: (look.items || []).map(({ handle, color, owned }) => ({ handle, color, owned: owned === true })),
    })),
    extras: (result.extras || []).map(({ handle, color }) => ({ handle, color })),
  });

  const sizedImage = (src, width) => (src ? src.replace(/([?&])width=\d+/, `$1width=${width}`) : '');

  const postCart = async (path, body) => {
    const response = await fetch(`${root}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw Object.assign(new Error(`HTTP ${response.status}`), { status: response.status });
    return response.json();
  };
  const getCart = () => fetch(`${root}cart.js`).then((res) => res.json());

  class AmorTrust extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      this.panel = this.querySelector('[data-trust-panel]');
      this.scroller = this.querySelector('[data-trust-scroll]');
      this.log = this.querySelector('[data-trust-log]');
      this.form = this.querySelector('[data-trust-form]');
      this.input = this.querySelector('[data-trust-input]');
      this.sendButton = this.querySelector('[data-trust-send]');
      this.newButton = this.querySelector('[data-trust-new]');
      this.status = this.querySelector('[data-trust-status]');
      this.heading = this.querySelector('[data-trust-title]');
      try {
        this.strings = JSON.parse(this.querySelector('[data-trust-strings]')?.textContent || '{}');
      } catch {
        this.strings = {};
      }
      this.token = 0;
      this.pending = false;
      this.failure = null;
      this.drawn = [];
      this.looks = new Map();

      document.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-trust-open]');
        if (!opener) return;
        event.preventDefault();
        this.open(opener);
      });
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
        else this.onClick(event);
      });
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.sendText(this.input.value);
      });
      // At the end of the conversation, it stays at the end while the look cards fill in.
      this.scroller.addEventListener(
        'scroll',
        () => (this.pinned = this.scroller.scrollHeight - this.scroller.scrollTop - this.scroller.clientHeight < 48),
        { passive: true }
      );
      document.addEventListener('demo-store:change', (event) => this.onStoreChange(event.detail?.key));
      // The size sheet's add opens the bag drawer (look.js); here the conversation goes on instead.
      window.addEventListener('cart:added', (event) => this.onCartAdded(event), true);
      document.addEventListener('drawer:close', (event) => {
        if (event.target.id === 'LookSheet') this.onSheetClosed();
        if (event.target.id === 'LoginDrawer') this.onLoginClosed();
      });
    }

    get isOpen() {
      return this.dialog.open;
    }

    /* --- Opening and closing ---------------------------------------------------- */

    /** Where it was opened from: the banner, a product page, or a content page with its subject. */
    contextOf(trigger) {
      const data = trigger?.dataset || {};
      if (data.trustOpen === 'product' && data.trustHandle) {
        return {
          type: 'product',
          key: `product:${data.trustHandle}`,
          product: {
            handle: data.trustHandle,
            title: data.trustTitle || '',
            vendor: data.trustVendor || '',
            // The color chosen on the page, as Build My Look takes it.
            color: trigger.closest('amor-product-form')?.color || null,
          },
        };
      }
      if (data.trustOpen === 'topic' && data.trustTopic) {
        return {
          type: 'topic',
          key: `topic:${data.trustTopic}`,
          opening: data.trustOpening || '',
          questions: String(data.trustQuestions || '').split('|').filter(Boolean),
        };
      }
      return { type: 'home', key: 'home' };
    }

    get key() {
      return this.context?.key || 'home';
    }

    open(trigger) {
      if (this.isOpen) return;
      const context = this.contextOf(trigger);
      // Another place, another conversation: what was on the way belongs to the old one.
      if (context.key !== this.key) {
        this.forget();
        this.drawnOpening = null;
      }
      this.context = context;
      this.trigger = trigger || null;
      this.source = trigger?.closest('.hero__box') || trigger || null;
      this.hero = trigger?.closest('amor-hero') || null;
      this.hero?.setPause?.('trust', true);
      this.trigger?.setAttribute('aria-expanded', 'true');
      this.render();
      this.dialog.showModal();
      document.documentElement.classList.add('is-locked');
      this.animateOpen();
      this.scrollToEnd();
      // On a phone the keyboard waits for a tap in the field.
      if (hoverDevice.matches) this.input.focus({ preventScroll: true });
      else this.heading.focus({ preventScroll: true });
    }

    close() {
      if (!this.isOpen || this.closing) return;
      this.closing = true;
      let closed = false;
      const done = () => {
        if (closed) return;
        closed = true;
        this.dialog.close();
        this.panel.getAnimations().forEach((running) => running.cancel());
        this.dialog.classList.remove('is-open');
        this.closing = false;
        if (!document.querySelector('dialog.drawer[open]')) document.documentElement.classList.remove('is-locked');
        this.hero?.setPause?.('trust', false);
        this.trigger?.setAttribute('aria-expanded', 'false');
        this.trigger?.focus({ preventScroll: true });
      };
      this.dialog.classList.remove('is-open');
      const animation = this.clipAnimation(true);
      if (!animation) {
        done();
        return;
      }
      animation.finished.then(done, done);
      // A tab in the background holds animations: the window closes on time anyway.
      window.setTimeout(done, cssTime('--duration-expand', 500) + 100);
    }

    /** The window grows out of the banner's box: its clip goes from the box to the whole window. */
    animateOpen() {
      window.requestAnimationFrame(() => this.dialog.classList.add('is-open'));
      this.clipAnimation(false);
    }

    /** Opening: from the box's rectangle to the window's; closing: back, fading. Null with reduce motion. */
    clipAnimation(closing) {
      this.panel.getAnimations().forEach((running) => running.cancel());
      if (reduceMotion.matches || !this.source?.isConnected) return null;
      const box = this.source.getBoundingClientRect();
      const panel = this.panel.getBoundingClientRect();
      if (!box.width || !panel.width) return null;
      const radius = parseFloat(cssValue('--radius-panel')) || 0;
      const panelRadius = parseFloat(getComputedStyle(this.panel).borderTopLeftRadius) || 0;
      const inset = (top, right, bottom, left, round) =>
        `inset(${Math.max(0, top)}px ${Math.max(0, right)}px ${Math.max(0, bottom)}px ${Math.max(0, left)}px round ${round}px)`;
      const from = inset(box.top - panel.top, panel.right - box.right, panel.bottom - box.bottom, box.left - panel.left, radius);
      const to = inset(0, 0, 0, 0, panelRadius);
      const timing = { duration: cssTime('--duration-expand', 500), easing: cssValue('--ease') || 'ease-out' };
      return closing
        ? this.panel.animate([{ clipPath: to, opacity: 1 }, { clipPath: from, opacity: 0 }], { ...timing, fill: 'forwards' })
        : this.panel.animate([{ clipPath: from }, { clipPath: to }], { ...timing, fill: 'backwards' });
    }

    onClick(event) {
      if (event.target.closest('[data-trust-close]')) {
        this.close();
        return;
      }
      if (event.target.closest('[data-trust-new]')) {
        this.startOver();
        return;
      }
      if (event.target.closest('[data-trust-retry]')) {
        this.retry();
        return;
      }
      const pay = event.target.closest('[data-trust-pay]');
      if (pay) {
        this.choose({ id: 'pay', at: Number(pay.dataset.trustPay) }, pay.querySelector('span')?.textContent || '');
        return;
      }
      const chip = event.target.closest('button[data-trust-chip]');
      if (chip) {
        const { trustChip: id, trustValue: value, trustAt: at } = chip.dataset;
        this.choose({ id, value: value || null, at: at === undefined || at === '' ? null : Number(at) }, chip.textContent);
      }
    }

    /* --- The conversation in the demo layer ------------------------------------ */

    /** The kept conversation, only when it was written in the same signed-in state. */
    chat() {
      const signedIn = Boolean(store()?.signedIn());
      const saved = store()?.trustChat(this.key);
      if (!saved || saved.signedIn !== signedIn || !Array.isArray(saved.messages)) return { signedIn, messages: [] };
      return saved;
    }

    messages() {
      return this.chat().messages;
    }

    save(messages) {
      store()?.setTrustChat(this.key, { signedIn: Boolean(store()?.signedIn()), messages });
    }

    push(...added) {
      this.save([...this.messages(), ...added]);
    }

    /** Drops what is on the way: its answer belongs to the old conversation. */
    forget() {
      this.token += 1;
      this.pending = false;
      this.failure = null;
      this.sheet = null;
      this.loginFrom = null;
    }

    startOver() {
      this.forget();
      store()?.setTrustChat(this.key, null);
      this.input.value = '';
      this.render();
      if (hoverDevice.matches) this.input.focus();
      else this.heading.focus({ preventScroll: true });
    }

    onStoreChange(key) {
      if (key === 'signedIn') {
        if (this.loginFrom && store()?.signedIn()) {
          this.continueAfterLogin();
          return;
        }
        this.forget();
        if (this.isOpen) this.render();
      }
      if (key === 'details' && this.isOpen) this.render();
    }

    /* --- Sending ---------------------------------------------------------------- */

    sendText(raw) {
      if (this.pending || this.sheet) return;
      const text = normalize(raw).slice(0, TEXT_MAX);
      if (!text) {
        this.input.focus();
        return;
      }
      this.input.value = '';
      this.answerTo({ role: 'customer', text });
    }

    /** A pill, or the pay button: the shopper's message is its words, the act is the pill's. */
    choose(act, label) {
      if (this.pending || this.sheet) return;
      if (act.id === 'register') return;
      this.answerTo({ role: 'customer', text: normalize(label), act });
    }

    answerTo(message) {
      let messages = this.messages();
      // A message that got no answer is replaced: two of the shopper's never stand in a row.
      if (messages.at(-1)?.role === 'customer') messages = messages.slice(0, -1);
      messages = [...messages, message];
      this.save(messages);
      this.handle(message, messages.length - 1);
    }

    retry() {
      const messages = this.messages();
      const last = messages.at(-1);
      if (!this.pending && last?.role === 'customer') this.handle(last, messages.length - 1);
    }

    handle(message, index) {
      let act = message.act || this.route(message.text, index);
      // A look made after "change" in the bag replaces what the earlier one put there.
      if (act.replace === undefined) {
        const stage = this.stageAt(index);
        act = { ...act, replace: stage?.via?.replace ?? stage?.message?.replace ?? null };
      }
      this.render();
      this.run(act, message.text);
    }

    /** The step the conversation is at before message `before`: AMOR's last message, or the one it stands for. */
    stageAt(before) {
      const messages = this.messages();
      for (let index = Math.min(before, messages.length) - 1; index >= 0; index -= 1) {
        const message = messages[index];
        if (message.role !== 'amor') continue;
        if (message.type === 'text' && Number.isInteger(message.as) && messages[message.as]) {
          return { message: messages[message.as], index: message.as, via: message };
        }
        return { message, index };
      }
      return null;
    }

    /** Free text, by the step it was written in (sections 4 and 7). */
    route(text, before) {
      const stage = this.stageAt(before);
      if (!stage) return { id: 'ask' };
      const { message, index: at, via } = stage;
      const answer = answerOf(text);
      switch (message.type) {
        case 'look': {
          const replace = via?.replace ?? message.replace ?? null;
          if (OTHER.test(normalize(text).replace(/[.!]/g, '')) && this.hasOther(message)) return { id: 'other', at };
          if (answer === 'yes') return { id: 'add', at, replace };
          if (answer === 'no') return { id: 'not-now', at };
          if (isInfo(text)) return { id: 'ask' };
          return { id: 'refine', at, replace };
        }
        case 'clarify': {
          const option = this.optionOf(text, message.options || []);
          return option ? { id: 'option', value: option, at } : { id: 'ask' };
        }
        case 'added':
          if (answer === 'yes') return { id: 'checkout', at };
          if (answer === 'no') return { id: 'not-now', at };
          return { id: 'ask' };
        case 'ship':
          if (has(text, OFFICE)) return { id: 'office', at };
          if (has(text, HOME)) return { id: 'home', at };
          if (answer === 'yes') return { id: 'office', at };
          if (answer === 'no') return { id: 'not-now', at };
          return { id: 'ask' };
        case 'summary':
          if (answer === 'yes') return { id: 'pay', at };
          if (answer === 'no') return { id: 'not-now', at };
          return { id: 'ask' };
        case 'checkout':
          if (has(text, LOGIN) || answer === 'yes') return { id: 'login', at };
          if (has(text, REGISTER)) return { id: 'go-checkout', at };
          if (answer === 'no') return { id: 'not-now', at };
          return { id: 'ask' };
        default:
          return { id: 'ask' };
      }
    }

    optionOf(text, options) {
      const value = normalize(text);
      const exact = options.find((option) => normalize(option) === value);
      if (exact) return exact;
      if (/גבר/.test(value)) return options.find((option) => /גבר/.test(option)) || null;
      if (/נש/.test(value)) return options.find((option) => /נש/.test(option)) || null;
      return null;
    }

    run(act, text) {
      const messages = this.messages();
      const at = Number.isInteger(act.at) ? act.at : null;
      const strings = this.strings;
      switch (act.id) {
        case 'ask':
        case 'example':
          this.ask(act.value || text);
          return;
        case 'shipping':
          this.ask(strings.shippingRequest);
          return;
        case 'refine':
          this.refine(act.value || text, at, act.replace ?? null);
          return;
        case 'option':
          this.clarify(act.value || text, at);
          return;
        case 'other':
          this.otherLook(at);
          return;
        case 'add':
          this.add(at, act.replace ?? null);
          return;
        case 'change':
          this.reply({ type: 'text', text: strings.changePrompt, as: at, replace: messages[at]?.replace ?? null, chips: this.refineChips() });
          return;
        case 'not-now': {
          const type = messages[at]?.type;
          this.reply({ type: 'text', text: type === 'look' ? strings.notNowLook : strings.notNowBag, as: at });
          return;
        }
        case 'more':
          this.reply({ type: 'text', text: strings.morePrompt, chips: this.exampleChips() });
          return;
        case 'change-bag': {
          const added = messages[at];
          this.reply({ type: 'text', text: strings.changeBagPrompt, as: added?.lookAt ?? null, replace: at, chips: this.refineChips() });
          return;
        }
        case 'checkout':
          this.checkout();
          return;
        case 'office':
        case 'home':
          this.summary(act.id);
          return;
        case 'pay':
          this.pay(at);
          return;
        case 'login':
          this.login();
          return;
        case 'go-checkout':
          window.location.assign(CHECKOUT_URL);
          return;
        case 'when': {
          const paid = messages[at];
          this.reply({ type: 'text', text: fill(strings.when, { place: this.placeText(paid?.place), date: paid?.date }), as: at });
          return;
        }
        case 'order-change':
          this.reply({ type: 'text', text: strings.orderChange, as: at });
          return;
        case 'product-look':
          this.productLook(DIRECTIONS[0], null);
          return;
        case 'product-policy':
          this.ask(strings.productPolicyRequest);
          return;
        case 'product-fitting': {
          const product = this.context?.product;
          if (!product) return;
          this.reply({
            type: 'text',
            text: fill(strings.productFitting, { product: this.productName() }),
            actions: [{ label: strings.fittingButton, url: ai().fittingUrl([product]) }],
          });
          return;
        }
        case 'human':
          this.reply({
            type: 'text',
            text: strings.human,
            actions: [
              { label: strings.call, url: `tel:${strings.phoneLink}` },
              { label: strings.contact, url: '/pages/contact' },
            ],
            as: at,
            chips: at === null ? this.exampleChips() : undefined,
          });
          return;
        default:
          this.ask(text);
      }
    }

    /** An answer that needs no request. */
    reply(message) {
      this.push({ role: 'amor', ...message });
      this.render(true);
    }

    /** A step that waits: the dots meanwhile; its messages, or the error with "try again". */
    async request(work) {
      const token = ++this.token;
      this.pending = true;
      this.failure = null;
      this.render();
      let added;
      try {
        added = await work();
      } catch (error) {
        if (token !== this.token) return;
        this.pending = false;
        this.failure = error?.code || 'network';
        this.render();
        return;
      }
      if (token !== this.token) return;
      this.pending = false;
      if (added?.length) this.push(...added.map((message) => ({ role: 'amor', ...message })));
      this.render(true);
    }

    /* --- The brain ---------------------------------------------------------------- */

    /** A question in the middle of a step (the shipping, the boutique's hours) keeps the step and its pills. */
    ask(text) {
      const stage = this.stageAt(this.messages().length);
      const keep = ['look', 'added', 'ship', 'summary', 'checkout', 'paid'].includes(stage?.message?.type) ? stage.index : null;
      // On a product page the question is about the item on the page: the brain gets its name with it.
      const about = this.context?.type === 'product' ? `${fill(this.strings.aboutProduct, { product: this.productName() })} ` : '';
      const question = `${about}${normalize(text)}`.slice(0, TEXT_MAX);
      this.request(async () => this.fromAsk(await ai().ask(question, null), question, keep));
    }

    /** "Rockstud Leather Pumps של Valentino": the item on the page, in AMOR's sentences. */
    productName() {
      const product = this.context?.product;
      if (!product) return '';
      return product.vendor ? fill(this.strings.productName, { title: product.title, vendor: product.vendor }) : product.title;
    }

    /**
     * "What goes with it" on a product page: Build My Look (/look) around the
     * item, in the color chosen on the page, one direction at a time. The item
     * itself leads the look, as in Build My Look. A direction already loaded,
     * here or in the Build My Look drawer, is not sent again (DemoStore).
     */
    productLook(direction, from) {
      const product = this.context?.product;
      if (!product) return;
      const previous = Number.isInteger(from) ? this.messages()[from] : null;
      const strings = this.strings;
      this.request(async () => {
        const color = previous?.context?.anchor?.color ?? product.color;
        const anchor = await ai().loadItem({ handle: product.handle, color, owned: true });
        if (!anchor) throw Object.assign(new Error('anchor'), { code: 'model_error' });
        const key = `${anchor.handle}:${anchor.color || ''}`;
        const state = store()?.builtLook(key) || { directions: {}, shown: [] };
        let saved = state.directions[direction];
        if (!saved) {
          const result = await ai().look({
            anchor: {
              handle: anchor.handle,
              color: anchor.color,
              owned: false,
              vendor: anchor.vendor,
              title: anchor.title,
              type: anchor.type,
              world: anchor.world,
            },
            direction,
            shown: state.shown,
          });
          const items = (result.items || []).map(({ handle, color: itemColor, reason, owned }) => ({
            handle,
            color: itemColor || null,
            reason: reason || '',
            owned: owned === true,
          }));
          saved = { status: result.status, items };
          state.directions[direction] = saved;
          state.shown = [...new Set([...(state.shown || []), ...items.map((item) => item.handle)])];
          store()?.setBuiltLook(key, state);
        }
        const word = strings.directions?.[direction] || {};
        const text = previous
          ? fill(strings.productOther, { direction: word.he || '' })
          : fill(strings.productLook, { product: this.productName() });
        if (!saved.items.length) return [{ type: 'text', text: strings.unavailable }];
        return [
          {
            type: 'look',
            text: saved.status === 'partial' ? `${text} ${strings.partial}` : text,
            result: {
              status: 'ok',
              opening: '',
              question: null,
              looks: [
                {
                  name: word.name || direction,
                  line: fill(strings.directionLine, { direction: word.he || '' }),
                  items: [{ handle: anchor.handle, color: anchor.color, reason: '', owned: false }, ...saved.items],
                },
              ],
              extras: [],
              gift_card: false,
              find_my_piece: false,
            },
            index: 0,
            context: {
              text: fill(strings.lookAround, { product: this.productName() }),
              mode: 'occasion',
              budget: 0,
              world: anchor.world || null,
              refines: [],
              anchor: { handle: anchor.handle, color: anchor.color },
              direction,
            },
          },
        ];
      });
    }

    fromAsk(data, text, keep = null) {
      if (EDIT_INTENTS.includes(data?.intent) && data.edit) {
        const params = data.params || {};
        return this.fromEdit(data.edit, {
          text: normalize(text),
          mode: params.mode || MODES[data.intent],
          budget: params.budget || 0,
          world: params.world || null,
          refines: [],
        });
      }
      if (!normalize(data?.reply)) throw Object.assign(new Error('empty'), { code: 'model_error' });
      const suggestions = Array.isArray(data.params?.suggestions) && data.params.suggestions.length ? data.params.suggestions : null;
      const unrelated = data.intent === 'unrelated';
      return [
        {
          type: 'text',
          text: data.reply,
          server: true,
          actions: this.actionsOf(data),
          chips: unrelated ? this.exampleChips(suggestions) : undefined,
          as: unrelated ? undefined : keep ?? undefined,
        },
      ];
    }

    /** A look result (/edit, or /ask's edit) as messages; `replace`: the bag message whose items it would replace. */
    fromEdit(result, context, replace = null) {
      const looks = result?.looks || [];
      if ((result?.status === 'ok' || result?.status === 'no_match') && looks.length) {
        return [{ type: 'look', text: result.opening || '', result, index: 0, context, replace }];
      }
      if (result?.status === 'clarify' && result.question) {
        return [
          {
            type: 'clarify',
            text: [result.opening, result.question.text].map(normalize).filter(Boolean).join(' '),
            options: result.question.options || [],
            context,
          },
        ];
      }
      if (result?.status === 'bad_image') return [{ type: 'text', text: this.strings.badImage }];
      const actions = result?.find_my_piece
        ? [{ label: this.strings.find, url: `/pages/find-my-piece?q=${encodeURIComponent(context.text)}` }]
        : [];
      return [{ type: 'text', text: result?.opening || '', server: true, actions, chips: this.exampleChips() }];
    }

    /** The request in words for /edit, with every detail asked for before (the oldest goes first when it is too long). */
    editBody(context) {
      const earlier = [...(context.refines || [])];
      const join = () => [context.text, ...earlier].map(normalize).filter(Boolean).join('. ');
      while (join().length > TEXT_MAX && earlier.length) earlier.shift();
      return {
        text: join().slice(0, TEXT_MAX),
        mode: context.mode === 'image' ? 'occasion' : context.mode,
        budget: context.budget || 0,
        world: context.world || null,
      };
    }

    refine(text, at, replace) {
      const look = this.messages()[at];
      if (!look || look.type !== 'look') {
        this.ask(text);
        return;
      }
      const value = normalize(text).slice(0, REFINE_MAX);
      const context = look.context || { text: '', refines: [] };
      this.request(async () => {
        const result = await ai().edit({ ...this.editBody(context), previous: previousOf(look.result), refine: value });
        return this.fromEdit(result, { ...context, refines: [...(context.refines || []), value] }, replace);
      });
    }

    clarify(option, at) {
      const question = this.messages()[at];
      const context = question?.context;
      if (!context) {
        this.ask(option);
        return;
      }
      const world = /גבר/.test(option) ? 'men' : /נש/.test(option) ? 'women' : context.world || null;
      this.request(async () => {
        const result = await ai().edit({ ...this.editBody({ ...context, world }), answer: option });
        return this.fromEdit(result, { ...context, world });
      });
    }

    otherLook(at) {
      const look = this.messages()[at];
      // A look around the item on the page: the next Build My Look direction.
      if (look?.context?.anchor && this.context?.type === 'product') {
        const next = DIRECTIONS[(DIRECTIONS.indexOf(look.context.direction) + 1) % DIRECTIONS.length];
        this.productLook(next, at);
        return;
      }
      const count = look?.result?.looks?.length || 0;
      if (count < 2) {
        this.refine(this.strings.chips.other, at, look?.replace ?? null);
        return;
      }
      this.reply({ ...look, text: this.strings.otherLook, index: (look.index + 1) % count });
    }

    /** The server's button and its second link; the size guide opens over the conversation. */
    actionsOf(data) {
      return [data?.action, data?.secondary]
        .map((action) => {
          if (!action) return null;
          if (action.type === 'size_guide') return { label: action.label || this.strings.sizeGuide, drawer: 'TrustSizeGuide' };
          if (!action.url || !/^(\/(?!\/)|https:\/\/|tel:)/.test(action.url)) return null;
          return { label: action.label || this.strings.go, url: action.url };
        })
        .filter(Boolean);
    }

    /* --- The bag ------------------------------------------------------------------ */

    /** A look's items as the store has them now (window.AmorAI.loadItem), kept per message. */
    lookItems(index) {
      const message = this.messages()[index];
      const key = `${index}:${message?.index}`;
      if (!this.looks.has(key)) {
        const look = message?.result?.looks?.[message.index];
        this.looks.set(
          key,
          Promise.all((look?.items || []).map((item) => ai().loadItem(item))).then((items) => items.filter(Boolean))
        );
      }
      return this.looks.get(key);
    }

    /** Her size for an item, from her profile: { variant, size }, or null when it is missing or sold out. */
    sizedVariant(item) {
      const row = item.row?.();
      if (!row) return null;
      if (row.dataset.variantId) return { variant: Number(row.dataset.variantId), size: this.strings.oneSize };
      const pill = [...row.querySelectorAll('[data-look-size]:not([disabled])')].find((button) => {
        const size = button.dataset.size || '';
        return size && store()?.sizeFor(size.split(' ')[0]) === size;
      });
      return pill ? { variant: Number(pill.dataset.lookSize), size: normalize(pill.textContent) } : null;
    }

    add(at, replace) {
      this.request(async () => {
        const items = await this.lookItems(at);
        const owned = items.some((item) => item.owned);
        const addable = items.filter((item) => !item.owned && item.available);
        if (!addable.length) return [{ type: 'text', text: owned ? this.strings.allOwned : this.strings.unavailable, as: at }];
        if (store()?.signedIn()) {
          const picks = addable.map((item) => ({ item, ...(this.sizedVariant(item) || {}) }));
          if (picks.every((pick) => pick.variant)) {
            try {
              await postCart('cart/add.js', { items: picks.map((pick) => ({ id: pick.variant, quantity: 1 })) });
              return this.afterAdd(at, picks.map(({ item, variant, size }) => this.addedItem(item, variant, size)), owned, replace, true);
            } catch (error) {
              // Sold out since the page was drawn: the size sheet shows what is left.
              if (error.status !== 422) throw error;
            }
          }
        }
        this.openSheet(at, addable, owned, replace);
        return [];
      });
    }

    addedItem(item, variant, size) {
      return { vendor: item.vendor || '', title: item.title || '', image: sizedImage(item.image, 160), size, variant };
    }

    /** The size sheet over the conversation (spec chapter 29), the same as in Your Private Edit. */
    openSheet(at, items, owned, replace) {
      this.sheet = { at, items, owned, replace };
      this.render();
      ai().openSizeSheet(items, this.sendButton);
    }

    onCartAdded(event) {
      if (!this.sheet || !this.isOpen) return;
      event.stopImmediatePropagation();
      this.finishSheet();
    }

    onSheetClosed() {
      if (this.sheet) this.finishSheet();
      else if (this.isOpen && hoverDevice.matches) this.input.focus({ preventScroll: true });
    }

    /** What the sheet added: the "done" message; nothing added: AMOR lets it be. */
    finishSheet() {
      const sheet = this.sheet;
      this.sheet = null;
      const rows = [...document.querySelectorAll('#LookSheet [data-look-row][data-state="added"]')];
      if (!rows.length) {
        this.reply({ type: 'text', text: this.strings.notNowLook, as: sheet.at });
        return;
      }
      const added = rows.map((row) => {
        const item = sheet.items.find((entry) => entry.handle === row.dataset.lookItem) || {};
        const pill = row.querySelector('[data-look-size][aria-pressed="true"]');
        return this.addedItem(item, Number(row.dataset.variantId), pill ? normalize(pill.textContent) : this.strings.oneSize);
      });
      this.request(async () => this.afterAdd(sheet.at, added, sheet.owned, sheet.replace, false));
      if (hoverDevice.matches) this.input.focus({ preventScroll: true });
    }

    /** The header's count, the bag drawer, and the items this look replaces (a change after adding). */
    async afterAdd(at, items, owned, replace, sized) {
      const messages = this.messages();
      const old = Number.isInteger(replace) ? messages[replace] : null;
      if (old?.variants?.length) {
        const cart = await getCart();
        const updates = {};
        old.variants.forEach((id) => {
          const line = (cart.items || []).find((entry) => entry.variant_id === id);
          if (line) updates[id] = Math.max(0, (updates[id] ?? line.quantity) - 1);
        });
        if (Object.keys(updates).length) await postCart('cart/update.js', { updates });
      }
      await this.syncBag();
      const text = old ? this.strings.addedReplaced : sized ? this.strings.addedSizes : this.strings.added;
      return [{ type: 'added', text, items, variants: items.map((item) => item.variant), owned, lookAt: at }];
    }

    async syncBag() {
      try {
        const cart = await getCart();
        window.AmorCart?.updateCartCount?.(cart.item_count);
      } catch {
        /* the count updates on the next page */
      }
      try {
        await window.AmorCartDrawer?.refresh();
      } catch {
        /* the drawer loads the bag again when it opens */
      }
    }

    /* --- The payment, mock ---------------------------------------------------------- */

    checkout() {
      this.request(async () => {
        const cart = await getCart();
        if (!cart.item_count) return [{ type: 'text', text: this.strings.emptyBag, chips: this.exampleChips() }];
        if (!store()?.signedIn()) return [{ type: 'checkout', text: this.strings.checkoutGuest }];
        return [{ type: 'ship', text: fill(this.strings.shipQuestion, { name: this.firstName() }) }];
      });
    }

    placeText(place) {
      return place === 'home' ? this.strings.toHome : this.strings.toOffice;
    }

    /** The delivery date as the bag says it: business days, Sunday to Thursday (cart.js). */
    deliveryText() {
      const date = new Date();
      let left = Number(this.dataset.deliveryDays) || 4;
      while (left > 0) {
        date.setDate(date.getDate() + 1);
        if (date.getDay() !== 5 && date.getDay() !== 6) left -= 1;
      }
      const weekday = String(this.dataset.weekdays || '').split(',')[date.getDay()] || '';
      return `${weekday}, ${date.getDate()}.${date.getMonth() + 1}`;
    }

    /** The bag's summary, read from the bag drawer as Shopify draws it now (the club benefit, shipping, total). */
    summary(place) {
      this.request(async () => {
        await window.AmorCartDrawer?.refresh();
        const drawer = document.getElementById('CartDrawer');
        const lines = [...(drawer?.querySelectorAll('[data-cart-line]') || [])];
        if (!lines.length) return [{ type: 'text', text: this.strings.emptyBag, chips: this.exampleChips() }];
        const checkout = drawer.querySelector('[data-checkout]');
        if (!checkout) return [{ type: 'text', text: this.strings.soldOutBag, actions: [{ label: this.strings.openBag, bag: true }] }];
        const items = lines.map((line) => ({
          vendor: line.dataset.vendor || '',
          title: line.dataset.title || '',
          image: sizedImage(line.querySelector('.cart-line__media img')?.getAttribute('src') || '', 160),
          options: [...line.querySelectorAll('.cart-line__options > span')].map((option) => normalize(option.textContent)).join(' · '),
          quantity: Number(line.dataset.quantity) || 1,
        }));
        const rows = [...drawer.querySelectorAll('[data-cart-part="summary"] .cart-summary__row')].map((row) => [
          normalize(row.querySelector('dt')?.textContent),
          normalize(row.querySelector('dd')?.textContent),
        ]);
        const total = normalize(checkout.lastElementChild?.textContent);
        return [{ type: 'summary', text: this.strings.summary, items, rows, total, date: this.deliveryText(), place }];
      });
    }

    pay(at) {
      const summary = this.messages()[at];
      if (summary?.type !== 'summary') return;
      this.request(async () => {
        await wait(PAY_MS);
        // Mock: no order and no stock change. The bag empties, like "empty the bag" (cart.js).
        await postCart('cart/clear.js', {});
        await postCart('cart/update.js', { attributes: { gift_wrap: '', gift_message: '' } });
        document.querySelectorAll('amor-cart').forEach((cart) => cart.resetGift?.());
        await this.syncBag();
        const number = store()?.nextOrderNumber() || '';
        return [
          {
            type: 'paid',
            text: fill(this.strings.paid, { number, place: this.placeText(summary.place), date: summary.date }),
            number,
            place: summary.place,
            date: summary.date,
          },
        ];
      });
    }

    /** A guest signs in from here: the conversation is kept, and goes on to the payment addressed to her. */
    login() {
      this.loginFrom = this.messages();
      this.render();
      if (!window.AmorAccount?.openLogin(this.sendButton, null)) {
        this.loginFrom = null;
        this.render();
      }
    }

    continueAfterLogin() {
      const messages = this.loginFrom;
      this.loginFrom = null;
      this.token += 1;
      this.pending = false;
      this.save(messages);
      this.request(async () => {
        await window.AmorAccount?.syncClubCode?.();
        const cart = await getCart();
        if (!cart.item_count) return [{ type: 'text', text: this.strings.emptyBag, chips: this.exampleChips() }];
        return [{ type: 'ship', text: fill(this.strings.shipQuestion, { name: this.firstName() }) }];
      });
    }

    onLoginClosed() {
      if (!this.loginFrom || store()?.signedIn()) return;
      const messages = this.loginFrom;
      this.loginFrom = null;
      const stage = this.stageAt(messages.length);
      this.reply({ type: 'text', text: this.strings.notNowBag, as: stage?.index ?? null });
    }

    firstName() {
      return window.AmorAccount?.firstName?.() || ai()?.customer()?.first_name || '';
    }

    /* --- Pills ---------------------------------------------------------------------- */

    exampleChips(list) {
      return (list || this.strings.examples || []).map((text) => ({ id: 'example', label: text, value: text }));
    }

    refineChips() {
      return (this.strings.refine || []).map((text) => ({ id: 'refine', label: text, value: text }));
    }

    /** The pills that fit the moment, under AMOR's last message (section 3). */
    chipsFor(message, index) {
      const chips = this.strings.chips || {};
      const own = (id) => ({ id, label: chips[id === 'change-bag' ? 'changeBag' : id === 'order-change' ? 'orderChange' : id], at: index });
      if (!message) return this.openingChips();
      switch (message.type) {
        case 'look':
          return [this.hasOther(message) ? own('other') : null, own('add'), own('change')].filter(Boolean);
        case 'clarify':
          return (message.options || []).map((text) => ({ id: 'option', label: text, value: text, at: index }));
        case 'added':
          return [own('shipping'), own('more'), own('checkout'), own('change-bag')];
        case 'ship':
          return [own('office'), own('home')];
        case 'checkout':
          return [own('login'), { id: 'register', label: chips.register, href: CHECKOUT_URL }];
        case 'paid':
          return [own('when'), own('order-change'), own('human')];
        case 'summary':
          return [];
        default: {
          if (Array.isArray(message.chips)) return message.chips.map((chip) => ({ ...chip, at: chip.at ?? message.as ?? index }));
          const messages = this.messages();
          if (Number.isInteger(message.as) && messages[message.as]) return this.chipsFor(messages[message.as], message.as);
          // An answer with no step of its own: the place's first pills again (the banner's, the item's, the page's).
          return this.openingChips();
        }
      }
    }

    /* --- Drawing ---------------------------------------------------------------------- */

    /** "Another direction": a second look in the same answer, or the next direction around the item on the page. */
    hasOther(message) {
      return (message.result?.looks || []).length > 1 || Boolean(message.context?.anchor && this.context?.type === 'product');
    }

    /** The first message, by where the conversation was opened from. */
    opening() {
      const name = store()?.signedIn() ? this.firstName() : '';
      const type = this.context?.type;
      if (type === 'product') {
        const values = { name, product: this.productName() };
        return fill(name ? this.strings.openingProductSignedIn : this.strings.openingProductGuest, values);
      }
      if (type === 'topic' && this.context.opening) return this.context.opening;
      return name ? fill(this.strings.openingSignedIn, { name }) : this.strings.openingGuest;
    }

    /** The pills under the first message: the banner's examples, the product's three, or the page's questions. */
    openingChips() {
      const chips = this.strings.chips || {};
      const type = this.context?.type;
      if (type === 'product') {
        return [
          { id: 'product-look', label: chips.productLook, at: null },
          { id: 'product-policy', label: chips.productPolicy, at: null },
          { id: 'product-fitting', label: chips.productFitting, at: null },
        ];
      }
      if (type === 'topic' && this.context.questions.length) {
        return this.exampleChips(this.context.questions).map((chip) => ({ ...chip, at: null }));
      }
      return [...this.exampleChips(), { id: 'human', label: chips.human, at: null }];
    }

    bubble(who, text, server) {
      const node = element('p', `trust__bubble trust__bubble--${who}`);
      if (who === 'customer') node.dir = 'auto';
      if (server) node.append(ai().linkify(text, this.strings.pages, this.strings.pageFallback));
      else node.textContent = text;
      return node;
    }

    thumb(src, alt) {
      const image = element('img', 'trust__thumb');
      image.alt = alt || '';
      image.width = 56;
      image.height = 75;
      image.loading = 'lazy';
      if (src) image.src = src;
      return image;
    }

    /** One row of a card: the picture, the brand, the name, and a line under it. */
    itemRow({ image, vendor, title, meta, badge }) {
      const row = element('li', 'trust__item');
      const info = element('div', 'trust__item-info');
      const brand = element('span', 'label trust__item-brand');
      brand.append(element('bdi', null, vendor));
      const name = element('span', 'trust__item-title');
      name.dir = 'ltr';
      name.textContent = title;
      info.append(brand, name);
      if (badge) info.append(element('span', 'badge label trust__owned', badge));
      if (meta) {
        const line = element('span', 'trust__item-meta');
        line.append(element('bdi', null, meta));
        info.append(line);
      }
      row.append(this.thumb(image, title), info);
      return row;
    }

    actionsRow(actions) {
      const row = element('div', 'trust__actions');
      actions.forEach((action, index) => {
        let node;
        if (action.drawer || action.bag) {
          node = element('button', index === 0 ? 'btn btn--sm' : 'link', action.label);
          node.type = 'button';
          if (action.drawer) node.dataset.drawerOpen = action.drawer;
          else node.dataset.cartOpen = '';
        } else {
          node = element('a', index === 0 ? 'btn btn--sm' : 'link', action.label);
          node.href = action.url;
          if (/^https:/.test(action.url)) {
            node.target = '_blank';
            node.rel = 'noopener';
          }
        }
        row.append(node);
      });
      return row;
    }

    lookCard(message, index) {
      const look = message.result?.looks?.[message.index] || {};
      const card = element('div', 'trust__card trust__look');
      card.setAttribute('role', 'group');
      card.setAttribute('aria-label', look.name || '');
      const head = element('div', 'trust__look-head');
      const name = element('h3', 'trust__look-name', look.name || '');
      name.dir = 'ltr';
      head.append(name);
      if (look.line) head.append(element('p', 'trust__look-line', look.line));
      const list = element('ul', 'trust__items');
      list.setAttribute('role', 'list');
      list.setAttribute('aria-busy', 'true');
      (look.items || []).forEach(() => {
        const row = element('li', 'trust__item trust__item--loading');
        row.append(element('span', 'skeleton trust__thumb'), element('span', 'skeleton skeleton--line'));
        list.append(row);
      });
      const total = element('p', 'trust__total');
      total.hidden = true;
      card.append(head, list, total);

      this.lookItems(index).then((items) => {
        list.removeAttribute('aria-busy');
        list.replaceChildren(
          ...items.map((item) =>
            this.itemRow({
              image: sizedImage(item.image, 160),
              vendor: item.vendor,
              title: item.title,
              meta: item.owned ? '' : ai().formatMoney(item.price, this.dataset.priceFormat),
              badge: item.owned ? this.strings.owned : '',
            })
          )
        );
        const addable = items.filter((item) => !item.owned && item.available);
        if (!items.length) list.replaceWith(element('p', 'trust__note', this.strings.unavailable));
        if (addable.length) {
          total.replaceChildren(
            element('span', null, this.strings.lookTotal),
            element('bdi', null, ai().formatMoney(addable.reduce((sum, item) => sum + item.price, 0), this.dataset.priceFormat))
          );
          total.hidden = false;
        }
        if (this.lastFresh === index) this.scrollToEnd(card.closest('.trust__msg'));
        else if (this.pinned) this.scroller.scrollTop = this.scroller.scrollHeight;
      });
      return card;
    }

    summaryCard(message, index) {
      const card = element('div', 'trust__card trust__summary');
      const list = element('ul', 'trust__items');
      list.setAttribute('role', 'list');
      list.setAttribute('aria-label', this.strings.summaryItems);
      (message.items || []).forEach((item) => {
        const meta = [item.options, item.quantity > 1 ? `× ${item.quantity}` : ''].filter(Boolean).join(' · ');
        list.append(this.itemRow({ image: item.image, vendor: item.vendor, title: item.title, meta }));
      });
      const rows = element('dl', 'trust__rows');
      (message.rows || []).forEach(([label, value]) => {
        const row = element('div', 'trust__row');
        row.append(element('dt', null, label), element('dd', null, value));
        rows.append(row);
      });
      const total = element('p', 'trust__total');
      total.append(element('span', null, this.strings.total), element('bdi', null, message.total));
      const arrives = element('p', 'trust__arrives', fill(this.strings.arrives, { place: this.placeText(message.place), date: message.date }));
      const pay = element('button', 'btn btn--block btn--split trust__pay');
      pay.type = 'button';
      pay.dataset.trustPay = index;
      pay.append(element('span', null, this.strings.pay), element('bdi', null, message.total));
      card.append(list, rows, total, arrives, pay);
      return card;
    }

    drawMessage(message, index) {
      const wrap = element('div', `trust__msg trust__msg--${message.role}`);
      wrap.dataset.index = index;
      if (message.role === 'customer') {
        wrap.append(this.bubble('customer', message.text));
        return wrap;
      }
      if (normalize(message.text)) wrap.append(this.bubble('amor', message.text, message.server));
      switch (message.type) {
        case 'look':
          wrap.append(this.lookCard(message, index));
          if (message.result?.gift_card) wrap.append(this.actionsRow([{ label: this.strings.gift, url: '/products/gift-card' }]));
          wrap.append(this.bubble('amor', this.strings.lookAsk));
          break;
        case 'added': {
          const card = element('div', 'trust__card');
          const list = element('ul', 'trust__items');
          list.setAttribute('role', 'list');
          (message.items || []).forEach((item) => list.append(this.itemRow({ ...item, meta: item.size })));
          card.append(list);
          if (message.owned) card.append(element('p', 'trust__note', this.strings.addedOwned));
          wrap.append(card);
          break;
        }
        case 'summary':
          wrap.append(this.summaryCard(message, index));
          break;
        default:
          if (message.actions?.length) wrap.append(this.actionsRow(message.actions));
      }
      return wrap;
    }

    dots() {
      const node = element('p', 'trust__bubble trust__bubble--amor trust__typing');
      const dots = element('span', 'trust__dots');
      dots.setAttribute('aria-hidden', 'true');
      dots.append(element('span'), element('span'), element('span'));
      node.append(dots, element('span', 'visually-hidden', this.strings.loading));
      return node;
    }

    error(code) {
      const box = this.querySelector('template[data-trust-error]').content.firstElementChild.cloneNode(true);
      box.querySelector('[data-trust-error-text]').textContent = code === 'rate_limited' ? this.strings.rateLimited : this.strings.error;
      box.querySelector('[data-trust-retry]').hidden = LIMITS.includes(code);
      box.querySelector('[data-trust-worlds]').hidden = !LIMITS.includes(code);
      return box;
    }

    chipsRow(chips) {
      const row = element('div', 'trust__chips');
      row.setAttribute('role', 'group');
      chips.forEach((chip) => {
        let node;
        if (chip.href) {
          node = element('a', 'pill trust__chip', chip.label);
          node.href = chip.href;
        } else {
          node = element('button', 'pill trust__chip', chip.label);
          node.type = 'button';
          node.dataset.trustChip = chip.id;
          if (chip.value) node.dataset.trustValue = chip.value;
          if (Number.isInteger(chip.at)) node.dataset.trustAt = chip.at;
        }
        row.append(node);
      });
      return row;
    }

    /**
     * Draws what changed: a new message is added at the end, a changed past
     * (a new conversation, signing in) draws everything again. Then the end:
     * the dots while AMOR writes, the error, or the pills of the moment.
     * `fresh` after a new answer of AMOR's.
     */
    render(fresh) {
      const messages = this.messages();
      const opening = this.opening();
      const same = this.drawnOpening === opening && this.drawn.length <= messages.length && this.drawn.every((json, i) => json === JSON.stringify(messages[i]));
      if (!same) {
        const first = element('div', 'trust__msg trust__msg--amor');
        first.append(this.bubble('amor', opening));
        this.log.replaceChildren(first);
        this.drawn = [];
        this.drawnOpening = opening;
        this.looks.clear();
      }
      this.tail?.remove();
      let lastNode = null;
      for (let index = this.drawn.length; index < messages.length; index += 1) {
        const node = this.drawMessage(messages[index], index);
        this.log.append(node);
        this.drawn.push(JSON.stringify(messages[index]));
        if (messages[index].role === 'amor') {
          lastNode = node;
          this.lastFresh = fresh ? index : null;
        }
      }

      const last = messages.at(-1);
      const stage = this.stageAt(messages.length);
      this.tail = element('div', 'trust__tail');
      if (this.pending) this.tail.append(this.dots());
      else if (this.sheet || this.loginFrom) {
        /* the size sheet or the login is over the conversation */
      } else if (last?.role === 'customer') this.tail.append(this.error(this.failure || 'network'));
      else {
        const lastAmor = messages.length ? messages[messages.length - 1] : null;
        const chips = lastAmor ? this.chipsFor(lastAmor, messages.length - 1) : this.chipsFor(null);
        if (chips.length) this.tail.append(this.chipsRow(chips));
      }
      this.log.append(this.tail);

      // Only the summary of the step she is at can be paid.
      this.log.querySelectorAll('[data-trust-pay]').forEach((button) => {
        button.disabled = this.pending || stage?.message?.type !== 'summary' || stage.index !== Number(button.dataset.trustPay);
      });

      const atLook = stage?.message?.type === 'look';
      this.input.placeholder = atLook ? this.input.dataset.placeholderLook : this.input.dataset.placeholder;
      this.sendButton.disabled = this.pending;
      this.form.setAttribute('aria-busy', String(this.pending));
      this.newButton.hidden = !messages.length;
      if (fresh && lastNode) {
        this.status.textContent = [...lastNode.querySelectorAll('.trust__bubble')].map((bubble) => normalize(bubble.textContent)).join(' ');
      }
      this.scrollToEnd(fresh ? lastNode : null);
    }

    /** The newest message in view; a new answer taller than the window shows from its top. */
    scrollToEnd(node) {
      window.requestAnimationFrame(() => {
        this.scroller.scrollTop = this.scroller.scrollHeight;
        this.pinned = true;
        if (!node?.isConnected) return;
        const gap = parseFloat(getComputedStyle(this.scroller).paddingTop) || 0;
        const top = node.getBoundingClientRect().top - this.scroller.getBoundingClientRect().top - gap;
        if (top < 0) {
          this.scroller.scrollTop += top;
          this.pinned = false;
        }
      });
    }
  }

  customElements.define('amor-trust', AmorTrust);
})();
